import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthService } from '../service/auth/auth.service';
import { DashboardMilestone, RestService } from '../service/rest/rest.service';
import { UserMilestoneBadgesService } from '../service/user-milestone-badges.service';
import { MessagesNotificationService } from '../service/messages-notification.service';
import { RevenueCatService } from '../service/billing/revenuecat.service';
import { SafeHtmlPipe } from '../shared/safe-html.pipe';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { PlatformService } from '../service/platform/platform.service';
import { SidebarService } from '../service/sidebar/sidebar.service';
import { LegalFooterComponent } from '../legal/legal-footer.component';
import { BrandService } from '../service/brand/brand.service';
import { BRAND } from '../service/brand/brand';

type NewsItem = { id: number; headline: string; text: string; createdAt: string };

const NEWS_SEEN_KEY = `${BRAND.storagePrefix}.news.lastSeenId`;

@Component({
  selector: 'app-dashboard-layout',
  standalone: true,
  imports: [CommonModule, DatePipe, RouterLink, RouterLinkActive, RouterOutlet, SafeHtmlPipe, LegalFooterComponent],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.css'
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  userName = '';
  recentMilestones: DashboardMilestone[] = [];
  unreadCount = 0;
  messagingEnabled = false;
  newMessagesDialog = false;
  dialogUnreadCount = 0;

  /** Drawer state lives in `SidebarService` so the navbar burger on tab
   *  pages opens the same menu. The template binds directly to
   *  `sidebar.isOpen()` for the burger icon swap and aria attributes. */
  protected readonly sidebar = inject(SidebarService);
  protected readonly brand = inject(BrandService);

  /** News bell state — mobile-visible only, but data loads on every device. */
  newsList: NewsItem[] = [];
  newsLoading = false;
  newsBellOpen = false;
  newsUnreadCount = 0;

  readonly isNative = isCapacitorNative();
  private readonly platform = inject(PlatformService);
  currentUrl: string;

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private newsTimer: ReturnType<typeof setInterval> | null = null;
  private routerSub: Subscription | null = null;
  private badgesSub: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private restService: RestService,
    private messagesNotification: MessagesNotificationService,
    private router: Router,
    private revenueCat: RevenueCatService,
    private ngLocation: Location,
    private milestoneBadgesService: UserMilestoneBadgesService,
  ) {
    const user = this.authService.getUser();
    this.userName = user?.name ?? '';
    this.currentUrl = router.url;
  }

  async ngOnInit() {
    void this.identifyBillingUser();
    await this.loadUnreadCount();
    void this.loadNews();
    const pending = this.messagesNotification.consumePendingLoginDialog();
    if (pending != null && pending > 0 && this.messagingEnabled) {
      this.dialogUnreadCount = pending;
      this.newMessagesDialog = true;
    }
    this.refreshTimer = setInterval(() => this.loadUnreadCount(), 60_000);
    this.newsTimer = setInterval(() => this.loadNews(), 5 * 60_000);
    this.routerSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe((event) => {
      this.currentUrl = event.urlAfterRedirects;
    });
    this.badgesSub = this.milestoneBadgesService.recentMilestones$.subscribe((milestones) => {
      this.recentMilestones = milestones.slice(0, 4);
    });
  }

  /**
   * iOS only: a header back arrow on dashboard children (settings, profile,
   * etc.). Android relies on the system back gesture instead, so duplicating
   * it in the header would be Material-3 noise.
   */
  canGoBack(): boolean {
    if (!this.platform.isIos()) return false;
    const path = (this.currentUrl || '/').split('?')[0].split('#')[0];
    return path !== '/' && path !== '/dashboard';
  }

  /**
   * Step one route up. Fall back to /dashboard when no history exists
   * (deep-link cold start) so Location.back() never exits the app.
   */
  goBack(): void {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.ngLocation.back();
      return;
    }
    void this.router.navigate(['/dashboard']);
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.badgesSub?.unsubscribe();
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.newsTimer) clearInterval(this.newsTimer);
  }

  private async loadUnreadCount() {
    const sid = this.authService.getUser()?.sid;
    if (!sid) return;
    const config = await this.restService.getContactConfig(sid);
    this.messagingEnabled = config?.contactEnabled === true;
    this.unreadCount = this.messagingEnabled ? (config?.unreadCount ?? 0) : 0;
  }

  private async loadNews() {
    const sid = this.authService.getUser()?.sid;
    if (!sid) {
      this.newsList = [];
      this.newsUnreadCount = 0;
      return;
    }
    this.newsLoading = true;
    try {
      const list = await this.restService.getActiveNews(sid);
      this.newsList = (list ?? []).slice(0, 10);
      this.newsUnreadCount = this.computeUnread(this.newsList);
    } catch {
      this.newsList = [];
      this.newsUnreadCount = 0;
    } finally {
      this.newsLoading = false;
    }
  }

  private computeUnread(items: NewsItem[]): number {
    const lastSeen = this.readLastSeenId();
    if (!items.length) return 0;
    if (lastSeen == null) return items.length;
    return items.filter((item) => item.id > lastSeen).length;
  }

  private readLastSeenId(): number | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(NEWS_SEEN_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private writeLastSeenId(id: number) {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(NEWS_SEEN_KEY, String(id));
  }

  /** Admin panel is web-only; never expose admin nav in the mobile APK. */
  isAdmin(): boolean {
    return !isCapacitorNative() && !!this.authService.getUser()?.admin;
  }

  toggleSidebar(): void {
    this.sidebar.toggle();
  }

  toggleNewsBell(): void {
    this.newsBellOpen = !this.newsBellOpen;
    if (this.newsBellOpen && this.newsList.length > 0) {
      // Mark the newest item as the highwater mark — visiting the dropdown
      // clears the badge until a newer news item appears.
      const highest = this.newsList.reduce((max, n) => (n.id > max ? n.id : max), 0);
      this.writeLastSeenId(highest);
      this.newsUnreadCount = 0;
    }
  }

  closeNewsBell(): void {
    this.newsBellOpen = false;
  }

  goToMessages() {
    this.newMessagesDialog = false;
    this.router.navigate(['/dashboard/messages']);
  }

  stayOnDashboard() {
    this.newMessagesDialog = false;
  }

  private identifyBillingUser(): void {
    if (!this.brand.showBilling) {
      return;
    }
    void this.revenueCat.ensureIdentified(this.authService.getUser());
  }
}
